import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router";

import { useConfigurationContext } from "../root/ConfigurationContext";
import { pastAuctionCutoff, zip5 } from "./leadUtils";
import type {
  AuctionLead,
  DealLead,
  LeadSortKey,
  ProbateLead,
  SortDir,
} from "./types";

export interface DealFilters {
  zip: string;
  date: string;
  county: string;
  tier: string;
  list: string; // groups.id — membership set is resolved by useListMembers
  showAuctions: boolean;
  showProbates: boolean;
  newOnly: boolean;
  sortKey: LeadSortKey | null;
  sortDir: SortDir;
}

const SORT_KEYS: LeadSortKey[] = [
  "type",
  "address",
  "owner",
  "date",
  "value",
  "tier",
];

/**
 * Filter/sort state lives in the URL (shareable, refresh-restorable), toggles
 * default to on. `set` writes only non-default values so the URL stays clean.
 */
export const useDealFilters = () => {
  const [params, setParams] = useSearchParams();

  const filters: DealFilters = useMemo(() => {
    const sortKey = params.get("sort") as LeadSortKey | null;
    return {
      zip: params.get("zip") ?? "",
      date: params.get("date") ?? "",
      county: params.get("county") ?? "",
      tier: params.get("tier") ?? "",
      list: params.get("list") ?? "",
      showAuctions: params.get("auctions") !== "0",
      showProbates: params.get("probates") !== "0",
      newOnly: params.get("newOnly") === "1",
      sortKey: sortKey && SORT_KEYS.includes(sortKey) ? sortKey : null,
      sortDir: params.get("dir") === "desc" ? "desc" : "asc",
    };
  }, [params]);

  const set = useCallback(
    (patch: Partial<DealFilters>) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const write = (key: string, value: string, defaultValue: string) => {
            if (value === defaultValue) next.delete(key);
            else next.set(key, value);
          };
          if (patch.zip !== undefined) write("zip", patch.zip, "");
          if (patch.date !== undefined) write("date", patch.date, "");
          if (patch.county !== undefined) write("county", patch.county, "");
          if (patch.tier !== undefined) write("tier", patch.tier, "");
          if (patch.list !== undefined) write("list", patch.list, "");
          if (patch.showAuctions !== undefined)
            write("auctions", patch.showAuctions ? "1" : "0", "1");
          if (patch.showProbates !== undefined)
            write("probates", patch.showProbates ? "1" : "0", "1");
          if (patch.newOnly !== undefined)
            write("newOnly", patch.newOnly ? "1" : "0", "0");
          if (patch.sortKey !== undefined)
            write("sort", patch.sortKey ?? "", "");
          if (patch.sortDir !== undefined) write("dir", patch.sortDir, "asc");
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  return { filters, set };
};

const distinct = (values: (string | null)[]): string[] =>
  [...new Set(values.filter((v): v is string => !!v))].sort();

/** Distinct dropdown options derived from the loaded data. */
export const buildFilterOptions = (
  auctions: AuctionLead[],
  probates: ProbateLead[],
) => ({
  dates: distinct(auctions.map((a) => a.saleDate)),
  counties: distinct([
    ...auctions.map((a) => a.county),
    ...probates.map((p) => p.county),
  ]),
  tiers: distinct(auctions.map((a) => a.dealTier)),
});

interface FilterContext {
  filters: DealFilters;
  hidePastAuctionsMonths?: number;
  listMemberIds: Set<string> | null; // source_record_ids on the selected list
  insidePolygon: (lead: DealLead) => boolean;
}

export const filterAuctions = (
  auctions: AuctionLead[],
  ctx: FilterContext,
): AuctionLead[] => {
  const { filters } = ctx;
  if (!filters.showAuctions) return [];
  const wantZip = zip5(filters.zip);
  const cutoff = pastAuctionCutoff(ctx.hidePastAuctionsMonths);
  return auctions.filter((a) => {
    if (cutoff && a.saleDate && a.saleDate < cutoff) return false;
    if (wantZip && zip5(a.zip) !== wantZip) return false;
    if (filters.date && a.saleDate !== filters.date) return false;
    if (filters.county && a.county !== filters.county) return false;
    if (filters.tier && a.dealTier !== filters.tier) return false;
    if (filters.list && !ctx.listMemberIds?.has(String(a.id))) return false;
    return ctx.insidePolygon(a);
  });
};

export const filterProbates = (
  probates: ProbateLead[],
  ctx: FilterContext,
): ProbateLead[] => {
  const { filters } = ctx;
  if (!filters.showProbates) return [];
  const wantZip = zip5(filters.zip);
  return probates.filter((p) => {
    if (wantZip && zip5(p.zip) !== wantZip) return false;
    if (filters.county && p.county !== filters.county) return false;
    if (filters.list && !ctx.listMemberIds?.has(String(p.id))) return false;
    return ctx.insidePolygon(p);
  });
};

/** Read the Settings→Deals hide-past-auctions preference (server-side config). */
export const useHidePastAuctionsMonths = (): number => {
  const { hidePastAuctionsMonths } = useConfigurationContext();
  return Number(hidePastAuctionsMonths ?? 0);
};
